import { useState } from "react";
import {
  baglantiSemasi,
  GOREV_RENGI_ETIKETI,
  GOREV_RENKLERI,
  yerelGunBasi,
  type GorevRengi,
} from "@koclukpanel/contracts";
import { BosDurum, GUN_KISA_ADLARI, Rozet, cn, type RozetTonu } from "@koclukpanel/ui";
import { TUR_ADI } from "./gorev-etiketleri";

/** Yalnız sunum alanları: koç önizlemesi öğrenci oturumu/istemcisi kullanmaz (F-SCH-17). */
export interface HaftalikGorunumGorevi {
  id: string;
  itemDate: string;
  sortOrder: number;
  kind: string;
  status: string;
  dersAdi?: string;
  konuAdi?: string | null;
  kaynakAdi?: string | null;
  renk?: GorevRengi;
  isExcused: boolean;
  targetQuestionCount: number | null;
  targetTestCount?: number | null;
  startTime: string | null;
  youtubeUrl: string | null;
  videoTitle?: string | null;
  topicPdfUrl: string | null;
  testPdfUrl: string | null;
  coachNote: string | null;
}

const TON: Record<GorevRengi, RozetTonu> = {
  YESIL: "onay",
  SARI: "uyari",
  KIRMIZI: "hata",
  NOTR: "notr",
};
const ISARET: Record<GorevRengi, string> = { YESIL: "✓", SARI: "◐", KIRMIZI: "!", NOTR: "◷" };
const FILTRELER = ["Tümü", "Bugün", "Tamamlanan", "Tamamlanmayan", "Bekleyen"] as const;
type Filtre = (typeof FILTRELER)[number];

/** F-STV-02a — istemci rengi hesaplamaz; yoksa durum uydurmaz. */
export function GorevRenkRozeti({ renk }: { renk?: GorevRengi }) {
  return renk ? (
    <Rozet ton={TON[renk]} isaret={ISARET[renk]}>
      {GOREV_RENGI_ETIKETI[renk]}
    </Rozet>
  ) : (
    <Rozet>Durum alınamadı</Rozet>
  );
}

export function ProgramBaglantisi({ url, etiket }: { url: string | null; etiket: string }) {
  const sonuc = baglantiSemasi.safeParse(url);
  if (!sonuc.success) return null;
  return (
    <a
      href={sonuc.data}
      target="_blank"
      rel="noopener noreferrer"
      className="min-h-dokunma rounded-alan bg-marka-50 text-marka-700 hover:bg-marka-100 inline-flex max-w-full items-center px-3 text-xs font-medium break-words"
    >
      {etiket}
    </a>
  );
}

export function haftaGunleri(hafta: string): string[] {
  const bas = new Date(`${hafta}T00:00:00.000Z`).getTime();
  return Array.from({ length: 7 }, (_, i) =>
    new Date(bas + i * 86400_000).toISOString().slice(0, 10),
  );
}

function gunBasligi(gun: string): string {
  return new Date(`${gun}T00:00:00Z`).toLocaleDateString("tr-TR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

/** A6 — gün seçimi varsayılan bugün; durum filtreleri haftanın tamamını tarar. Veri girişi yok. */
export function HaftalikProgramGorunumu({
  hafta,
  gorevler,
}: {
  hafta: string;
  gorevler: readonly HaftalikGorunumGorevi[];
}) {
  const gunler = haftaGunleri(hafta);
  const bugun = yerelGunBasi().toISOString().slice(0, 10);
  const [secili, setSecili] = useState(() => (gunler.includes(bugun) ? bugun : hafta));
  const [filtre, setFiltre] = useState<Filtre>("Tümü");
  const haftanin = gorevler.filter((g) => gunler.includes(g.itemDate.slice(0, 10)));
  const gorunen = haftanin
    .filter((g) => {
      switch (filtre) {
        case "Tümü":
          return g.itemDate.slice(0, 10) === secili;
        case "Bugün":
          return g.itemDate.slice(0, 10) === bugun;
        case "Tamamlanan":
          return g.renk === "YESIL";
        case "Tamamlanmayan":
          return g.renk === "SARI" || g.renk === "KIRMIZI";
        case "Bekleyen":
          return g.renk === "NOTR" && !g.isExcused && g.status !== "POSTPONED";
      }
    })
    .sort((a, b) => a.itemDate.localeCompare(b.itemDate) || a.sortOrder - b.sortOrder);
  const soruSayisi = gorunen.reduce((t, g) => t + (g.targetQuestionCount ?? 0), 0);
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div role="group" aria-label="Görev renkleri" className="flex flex-wrap gap-2">
        {GOREV_RENKLERI.map((renk) => (
          <GorevRenkRozeti key={renk} renk={renk} />
        ))}
      </div>
      <div role="group" aria-label="Görev filtresi" className="flex flex-wrap gap-2">
        {FILTRELER.map((f) => (
          <button
            key={f}
            type="button"
            aria-pressed={filtre === f}
            onClick={() => setFiltre(f)}
            className={cn(
              "min-h-dokunma rounded-alan px-3 text-sm",
              filtre === f ? "bg-marka-600 text-notr-0" : "bg-notr-100 text-notr-700",
            )}
          >
            {f}
          </button>
        ))}
      </div>
      {/* Koç uygulamasının CSS taraması öğrenci klasörünü kapsamaz; sütun
          sayısı her iki uygulamada aynı yerleşimi sağlamalı (F-SCH-17). */}
      <ul className="grid gap-1" style={{ gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
        {gunler.map((gun, i) => {
          const adet = haftanin.filter((g) => g.itemDate.slice(0, 10) === gun).length;
          const bu = secili === gun && filtre === "Tümü";
          return (
            <li key={gun} className="min-w-0">
              <button
                type="button"
                aria-pressed={bu}
                onClick={() => {
                  setSecili(gun);
                  setFiltre("Tümü");
                }}
                className={cn(
                  "min-h-dokunma rounded-alan flex w-full flex-col items-center justify-center px-1 py-1.5 text-xs",
                  bu
                    ? "bg-marka-600 text-notr-0 font-semibold"
                    : "bg-notr-0 text-notr-700 ring-notr-200 ring-1 ring-inset",
                )}
              >
                <span>{GUN_KISA_ADLARI[i]}</span>
                <span className="tabular-nums">{adet === 0 ? "—" : adet}</span>
              </button>
            </li>
          );
        })}
      </ul>
      <div>
        <h2 className="text-notr-900 text-sm font-semibold">
          {filtre === "Tümü"
            ? gunBasligi(secili)
            : filtre === "Bugün"
              ? gunBasligi(bugun)
              : `Haftanın görevleri · ${filtre}`}
        </h2>
        <p className="text-notr-600 text-xs">
          {gorunen.length === 0
            ? "Planlanmış görev yok"
            : `${gorunen.length} görev${soruSayisi > 0 ? ` · ${soruSayisi} soru` : ""}`}
        </p>
      </div>
      {gorunen.length === 0 ? (
        <BosDurum
          baslik={filtre === "Tümü" ? "Bu gün boş" : "Filtreye uygun görev yok"}
          aciklama={
            filtre === "Tümü"
              ? "Koçun bu güne bir şey planlamamış. Boş gün de programın parçası."
              : "Başka bir filtre veya gün seçebilirsin."
          }
        />
      ) : (
        <ul className="flex min-w-0 flex-col gap-2">
          {gorunen.map((g) => (
            <li
              key={g.id}
              className="rounded-alan bg-notr-0 ring-notr-200 flex min-w-0 flex-col gap-2 p-3 break-words ring-1"
            >
              <div className="flex flex-wrap items-start gap-2">
                <span className="text-notr-700 text-sm tabular-nums">{g.startTime ?? "—"}</span>
                <span className="min-w-0 flex-1">
                  <span className="text-notr-900 block text-sm font-medium break-words">
                    {g.dersAdi ?? "Ders"}
                    {g.konuAdi && ` · ${g.konuAdi}`}
                  </span>
                  <span className="text-notr-600 block text-xs">
                    {TUR_ADI[g.kind] ?? g.kind}
                    {g.targetQuestionCount !== null && ` · hedef ${g.targetQuestionCount} soru`}
                    {g.targetTestCount != null && ` · ${g.targetTestCount} test`}
                  </span>
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <GorevRenkRozeti renk={g.renk} />
                {g.isExcused && <Rozet>Mazeretli</Rozet>}
              </div>
              {filtre !== "Tümü" && filtre !== "Bugün" && (
                <p className="text-notr-600 text-xs">{gunBasligi(g.itemDate.slice(0, 10))}</p>
              )}
              {g.kaynakAdi && <p className="text-notr-700 text-xs">Kaynak: {g.kaynakAdi}</p>}
              <div className="flex flex-wrap gap-2">
                <ProgramBaglantisi url={g.topicPdfUrl} etiket="Konu PDF" />
                <ProgramBaglantisi url={g.testPdfUrl} etiket="Test PDF" />
                <ProgramBaglantisi
                  url={g.youtubeUrl}
                  etiket={g.videoTitle ? `Video · ${g.videoTitle}` : "Video"}
                />
              </div>
              {g.coachNote && (
                <p className="text-notr-600 text-xs whitespace-pre-wrap">
                  <span className="font-medium">Koç notu · </span>
                  {g.coachNote}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
